import { useEffect, useState } from "react";
import { api, authedFileUrl } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Download, Link as LinkIcon } from "lucide-react";

export default function PayrollExport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    const now = new Date();
    setFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
    setTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10));
    api.get("/pay-statements").then((r) => setStatements(r.data)).catch(() => {});
  }, []);

  const inRange = statements.filter((s) => {
    const d = s.pay_date || s.created_at.slice(0, 10);
    return (!from || d >= from) && (!to || d <= to);
  });
  const totalGross = inRange.reduce((s, x) => s + (x.totals?.gross || 0), 0);
  const totalDed = inRange.reduce((s, x) => s + (x.totals?.deductions || 0), 0);
  const totalNet = inRange.reduce((s, x) => s + (x.totals?.net || 0), 0);

  const downloadCSV = () => {
    window.open(authedFileUrl(`/quickbooks/export.csv?period_start=${from}&period_end=${to}`), "_blank");
  };

  return (
    <div className="space-y-6" data-testid="export-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Payroll Export</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Send approved pay runs to payroll. The CSV export always works — the live QuickBooks connection needs accounting API access configured for this project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="eyebrow">Pay runs in period</div><div className="text-xl font-semibold mt-1">{inRange.length}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="eyebrow">Gross Pay</div><div className="text-xl font-semibold mt-1">${totalGross.toFixed(2)}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="eyebrow">Deductions</div><div className="text-xl font-semibold mt-1">${totalDed.toFixed(2)}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="eyebrow">Net Pay</div><div className="text-xl font-semibold mt-1">${totalNet.toFixed(2)}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="eyebrow">Connect to QuickBooks</div>
          <div className="text-xs text-slate-500 mt-0.5">Optional live connection through the project's secure connection service.</div>
          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-2">
              <span className="pill-gray">Not connected</span>
              <span className="text-xs text-slate-500">No accounting API access is configured for this project yet.</span>
            </div>
          </div>
          <div className="mt-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900 p-3">
            Live QuickBooks connection is not available: no accounting API access is configured. Use the payroll CSV export — it is the reliable path for QuickBooks payroll import and for any generic payroll system.
          </div>
          <div className="flex gap-2 mt-4">
            <Button disabled variant="outline"><LinkIcon size={14} className="mr-1" /> Connect to QuickBooks</Button>
            <Button variant="outline">Check connection</Button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="eyebrow">Payroll Export File</div>
          <div className="text-xs text-slate-500 mt-0.5">One row per driver per pay run in the selected period.</div>
          <div className="mt-4 flex items-center gap-2">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={downloadCSV} data-testid="download-payroll-csv">
              <Download size={14} className="mr-1" /> Download payroll CSV
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-3">
            Header row exactly as your import expects: Date, Employee, Memo, Account, Debit, Credit.
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="eyebrow">Statements in range</div>
          <div className="text-xs text-slate-500">Pay date between {from} and {to}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Statement #</th>
                <th className="px-4 py-2.5">Pay Date</th>
                <th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Gross</th>
                <th className="px-4 py-2.5 text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {inRange.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No pay runs in this period.</td></tr>}
              {inRange.map((s) => (
                <tr key={s.statement_id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-mono text-xs">{s.statement_number}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{s.pay_date || s.created_at.slice(0, 10)}</td>
                  <td className="px-4 py-2.5">{s.driver_name}</td>
                  <td className="px-4 py-2.5">
                    <span className={(s.status || "draft") === "paid" ? "pill-green" : "pill-amber"}>
                      {(s.status || "draft").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">${s.totals.gross.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold">${s.totals.net.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
